/* ===========================================================================
   c-lp-approach-flow.js — scroll-driven step flow for "How buying a vacuum
   should actually go".

   The Figma note on 1291:3481 reads "This must change on scroll. vertically.",
   and the mobile frame puts a down arrow under the tile as the affordance. So
   with JavaScript this section becomes a pinned sequence: a tall scroller, a
   sticky stage, and one step visible at a time that advances as the page is
   scrolled. The prev/next buttons jump the page to a step for anyone who
   would rather click, and the rail shows how far along the flow is.

   Progressive enhancement only (CONVENTIONS, review round 1, rule 2). Without
   JavaScript — which includes every Linux visitor, because the store's cloak
   script strips scripts for them — the root keeps no data-af-mode, nothing
   sticks, the controls keep the `hidden` they ship with, and the steps stay a
   native horizontal scroll-snap rail that swipes and trackpad-scrolls.

   No third-party library: a carousel library does not pin a section to the
   viewport, and it would cost far more than the few lines below. Scoped per
   [data-sl-id] root, bound once, re-inits on shopify:section:load.

   Inactive steps are faded with opacity, never display/visibility, so all of
   them stay in the accessibility tree and a screen reader reads the sequence.
   =========================================================================== */
(function () {
  'use strict';
  if (window.__cLpApproachFlow) return;
  window.__cLpApproachFlow = true;

  var SELECTOR = '[data-sl-id="c-lp-approach-flow"]';

  function supportsSticky() {
    return typeof CSS !== 'undefined' && CSS.supports && CSS.supports('position', 'sticky');
  }

  function init(root) {
    if (!root || root.dataset.afReady === '1') return;
    var scroller = root.querySelector('[data-af-scroller]');
    var stage = root.querySelector('[data-af-stage]');
    var steps = [].slice.call(root.querySelectorAll('[data-af-step]'));
    if (!scroller || !stage || steps.length < 2 || !supportsSticky()) return;

    root.dataset.afReady = '1';
    root.dataset.afMode = 'scroll';

    var prev = root.querySelector('[data-af-prev]');
    var next = root.querySelector('[data-af-next]');
    var fill = root.querySelector('[data-af-fill]');
    var motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var index = -1;
    var frame = 0;

    [prev, next].forEach(function (el) { if (el) el.hidden = false; });

    function stickyTop() {
      return parseFloat(window.getComputedStyle(stage).top) || 0;
    }

    function travel() {
      // Distance the page scrolls while the stage stays pinned.
      return Math.max(1, scroller.offsetHeight - stage.offsetHeight);
    }

    function activate(i) {
      if (i === index) return;
      index = i;
      steps.forEach(function (step, n) {
        var on = n === i;
        step.classList.toggle('is-active', on);
        if (on) { step.setAttribute('aria-current', 'step'); } else { step.removeAttribute('aria-current'); }
      });
      if (fill) fill.style.width = (((i + 1) / steps.length) * 100).toFixed(2) + '%';
      if (prev) prev.disabled = i <= 0;
      if (next) next.disabled = i >= steps.length - 1;
      // The "keep scrolling" hint has nothing left to point at on the last step.
      root.dataset.afEnd = i >= steps.length - 1 ? '1' : '0';
    }

    function measure() {
      var rect = scroller.getBoundingClientRect();
      var progress = (stickyTop() - rect.top) / travel();
      var i = Math.floor(progress * steps.length);
      activate(Math.min(steps.length - 1, Math.max(0, i)));
    }

    function onScroll() {
      if (frame) return;
      frame = window.requestAnimationFrame(function () { frame = 0; measure(); });
    }

    function goTo(i) {
      var target = Math.min(steps.length - 1, Math.max(0, i));
      var rect = scroller.getBoundingClientRect();
      var segment = travel() / steps.length;
      // Put the middle of that step's segment under the sticky offset.
      var delta = rect.top - stickyTop() + (target + 0.5) * segment;
      window.scrollBy({ top: delta, behavior: motion.matches ? 'auto' : 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { goTo(index - 1); });
    if (next) next.addEventListener('click', function () { goTo(index + 1); });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    if (window.ResizeObserver) new ResizeObserver(onScroll).observe(scroller);

    activate(0);
    measure();
  }

  function boot(scope) {
    [].slice.call((scope || document).querySelectorAll(SELECTOR)).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
})();
